import React, { useState } from "react";
import { TextField, Button, Box, useMediaQuery, useTheme } from "@mui/material";
import { AddressData, initialAddressData } from "../../types/Address";
import { EmergencyContactData } from "../../types/EmergencyContact";
import { UserData } from "../../types/User";

interface ComponentProps {
  onSubmit: (user: Partial<EmergencyContactData>) => void;
  onClose: () => void;
  detailData: UserData;
  initialData?: Partial<EmergencyContactData>;
}

const EmergencyContactForm: React.FC<ComponentProps> = ({
  onSubmit,
  onClose,
  detailData,
  initialData,
}) => {
  const [inputData, setInputData] = useState<Partial<EmergencyContactData>>(
    initialData || initialAddressData
  );
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // 🌐 detect screen size
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;

    setInputData((prev: Partial<AddressData>) => ({
      ...prev,
      userId: detailData.id,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (name == "countryId" && value == "LR") {
      setInputData((prev) => ({
        ...prev,
        countyId: null,
      }));
    } else if (name == "countryId" && value != "LR") {
      setInputData((prev) => ({
        ...prev,
        region: null,
      }));
    }
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const newErrors: { [key: string]: string } = {};
    if (!inputData.name) newErrors.name = "Contact name is required";
    if (!inputData.primaryPhone)
      newErrors.primaryPhone = "Primary phone is required";

    return newErrors;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    onSubmit(inputData);
  };

  return (
    <form onSubmit={handleSubmit} style={{ marginTop: 16 }}>
      {/* 🔹 Responsive grid layout */}
      <Box display="grid" gridTemplateColumns="1fr" gap={2}>
        <TextField
          name="name"
          label="Name"
          value={inputData.name}
          onChange={handleChange}
          fullWidth
          margin="normal"
          error={!!errors.name}
          helperText={errors.name}
        />

        <TextField
          name="primaryPhone"
          label="Primary Phone"
          value={inputData.primaryPhone}
          onChange={handleChange}
          fullWidth
          margin="normal"
          error={!!errors.primaryPhone}
          helperText={errors.primaryPhone}
        />

        <TextField
          name="secondaryPhone"
          label="Secondary Phone"
          value={inputData.secondaryPhone}
          onChange={handleChange}
          fullWidth
          margin="normal"
        />

        <TextField
          name="email"
          label="Email"
          value={inputData.email}
          onChange={handleChange}
          fullWidth
          margin="normal"
        />
      </Box>

      {/* Action buttons */}
      <Box
        display="flex"
        flexDirection={isMobile ? "column" : "row"}
        gap={2}
        marginTop={3}
      >
        <Button
          variant="outlined"
          color="secondary"
          onClick={onClose}
          fullWidth
        >
          Close
        </Button>
        <Button type="submit" variant="contained" color="success" fullWidth>
          Save
        </Button>
      </Box>
    </form>
  );
};

export default EmergencyContactForm;
